import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [articleDropdownOpen, setArticleDropdownOpen] = useState(false);
  const articleDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (articleDropdownRef.current && !articleDropdownRef.current.contains(e.target as Node)) {
        setArticleDropdownOpen(false);
      }
    };
    if (articleDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [articleDropdownOpen]);

  const articleLinks = [
    { name: 'Complete Guide', path: '/vigorbuy-spreadsheet-guide' },
    { name: 'Templates', path: '/vigorbuy-spreadsheet-templates' },
    { name: 'Comparison', path: '/vigorbuy-spreadsheet-comparison' },
    { name: 'For Beginners', path: '/vigorbuy-spreadsheet-beginners' },
    { name: 'Tips & Tricks', path: '/vigorbuy-spreadsheet-tips' },
    { name: 'Security', path: '/vigorbuy-spreadsheet-security' },
    { name: 'Features', path: '/vigorbuy-spreadsheet-features' },
    { name: 'Pricing', path: '/vigorbuy-spreadsheet-pricing' },
    { name: 'FAQ', path: '/vigorbuy-spreadsheet-faq' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-file-excel-2-line text-background-50 text-lg"></i>
            </div>
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-900">
              VigorBuy<span className="text-primary-500">Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors rounded-lg hover:bg-background-100 whitespace-nowrap"
            >
              <i className="ri-home-4-line mr-1"></i>Home
            </Link>
            <a href="#features" className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors rounded-lg hover:bg-background-100 whitespace-nowrap">Features</a>
            <a href="#categories" className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors rounded-lg hover:bg-background-100 whitespace-nowrap">Categories</a>

            {/* Articles Dropdown */}
            <div ref={articleDropdownRef} className="relative">
              <button
                onClick={() => setArticleDropdownOpen(!articleDropdownOpen)}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-accent-600 hover:text-accent-700 transition-colors rounded-lg hover:bg-accent-50 whitespace-nowrap cursor-pointer"
              >
                Shopping Guide
                {articleDropdownOpen ? (
                  <i className="ri-arrow-up-s-line text-xs"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-xs"></i>
                )}
              </button>
              {articleDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-background-50 border border-background-200 rounded-lg shadow-sm py-1 z-50">
                  {articleLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setArticleDropdownOpen(false)}
                      className="block px-4 py-2 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a href="#how-it-works" className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors rounded-lg hover:bg-background-100 whitespace-nowrap">How It Works</a>
            <a href="#security" className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors rounded-lg hover:bg-background-100 whitespace-nowrap">Security</a>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              Visit Official Site
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl text-foreground-800 ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden pb-4 border-t border-background-200 mt-0 pt-3 max-h-[70vh] overflow-y-auto">
            <div className="flex flex-col gap-1">
              <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 whitespace-nowrap">
                <i className="ri-home-4-line"></i> Home
              </Link>
              <a href="#features" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 whitespace-nowrap">Features</a>

              {/* Mobile Categories Section */}
              <div className="mt-2 mb-1 px-3 py-1">
                <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">Shop Categories</span>
              </div>
              <div className="grid grid-cols-2 gap-1 px-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-1.5 px-2 py-2 text-xs font-medium text-foreground-600 hover:text-primary-600 rounded-lg hover:bg-primary-50 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-right-up-line text-primary-400 text-xs"></i>
                    {cat.name}
                  </a>
                ))}
              </div>

              {/* Mobile Articles Section */}
              <div className="mt-3 mb-1 px-3 py-1">
                <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">Shopping Guide</span>
              </div>
              {articleLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-accent-600 rounded-lg hover:bg-accent-50 whitespace-nowrap"
                >
                  {link.name}
                </Link>
              ))}

              <div className="mt-2 pt-2 border-t border-background-200">
                <a href="#how-it-works" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 whitespace-nowrap block">How It Works</a>
                <a href="#security" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 whitespace-nowrap block">Security</a>
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="mt-2 px-4 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg text-center transition-colors whitespace-nowrap cursor-pointer"
              >
                Visit Official Site
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}